import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemePreference = 'light' | 'dark' | 'system';
export type InterfaceDensity = 'compact' | 'comfortable';
export type AccentPreference = 'navy' | 'blue' | 'teal';

interface UserUIPreferences {
  collapsed: boolean;
  theme: ThemePreference;
  density: InterfaceDensity;
  accent: AccentPreference;
  expandedNavigation: readonly string[];
}

const defaults: UserUIPreferences = {
  collapsed: false,
  theme: 'system',
  density: 'compact',
  accent: 'navy',
  expandedNavigation: [],
};

interface UIState extends UserUIPreferences {
  mobileOpen: boolean;
  userId?: string;
  preferencesByUser: Record<string, UserUIPreferences>;
  bindUser(userId: string | undefined): void;
  toggleCollapsed(): void;
  setMobileOpen(value: boolean): void;
  setTheme(value: ThemePreference): void;
  toggleTheme(): void;
  setDensity(value: InterfaceDensity): void;
  setAccent(value: AccentPreference): void;
  toggleNavigation(id: string, parentId?: string): void;
  setExpandedNavigation(ids: readonly string[]): void;
}

function persistCurrent(state: UIState, patch: Partial<UserUIPreferences>): Partial<UIState> {
  const next = { ...pickPreferences(state), ...patch };
  return {
    ...patch,
    preferencesByUser: state.userId
      ? { ...state.preferencesByUser, [state.userId]: next }
      : state.preferencesByUser,
  };
}

function pickPreferences(state: UserUIPreferences): UserUIPreferences {
  return {
    collapsed: state.collapsed,
    theme: state.theme,
    density: state.density,
    accent: state.accent,
    expandedNavigation: state.expandedNavigation,
  };
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      ...defaults,
      mobileOpen: false,
      preferencesByUser: {},
      bindUser: (userId) => set((state) => {
        const preferences = userId ? state.preferencesByUser[userId] ?? defaults : defaults;
        const resetPreferences = { ...preferences, expandedNavigation: [] };
        return {
          userId,
          ...resetPreferences,
          mobileOpen: false,
          preferencesByUser: userId
            ? { ...state.preferencesByUser, [userId]: resetPreferences }
            : state.preferencesByUser,
        };
      }),
      toggleCollapsed: () => set((state) => persistCurrent(state, { collapsed: !state.collapsed })),
      setMobileOpen: (mobileOpen) => set({ mobileOpen }),
      setTheme: (theme) => set((state) => persistCurrent(state, { theme })),
      toggleTheme: () => set((state) => persistCurrent(state, {
        theme: state.theme === 'dark' ? 'light' : 'dark',
      })),
      setDensity: (density) => set((state) => persistCurrent(state, { density })),
      setAccent: (accent) => set((state) => persistCurrent(state, { accent })),
      toggleNavigation: (id, parentId) => set((state) => {
        const expandedNavigation = state.expandedNavigation.includes(id)
          ? parentId
            ? state.expandedNavigation.filter((item) => item !== id)
            : []
          : parentId
            ? [parentId, id]
            : [id];
        return persistCurrent(state, { expandedNavigation });
      }),
      setExpandedNavigation: (expandedNavigation) => set((state) => {
        const normalized = [...new Set(expandedNavigation)];
        return state.expandedNavigation.length === normalized.length
          && state.expandedNavigation.every((item, index) => item === normalized[index])
          ? state
          : persistCurrent(state, { expandedNavigation: normalized });
      }),
    }),
    {
      name: 'ochal.ui.v2',
      partialize: (state) => ({ preferencesByUser: state.preferencesByUser }),
    },
  ),
);
